import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey, Role as CustomerRole } from "src/network/User";
import { MessageType } from "src/components/notifications/Notifier";
import { getEnvironment } from "src/utils/Customer";
import { GET_COPY_CUSTOMER_JOB_STATUSES, GET_COPY_CUSTOMER_SOURCE_CUSTOMERS } from "src/api/customers";
import { GetCopyCustomerJobStatuses } from "src/api/generated/GetCopyCustomerJobStatuses";
import {
    GetCopyCustomerSourceCustomers,
    GetCopyCustomerSourceCustomers_devCustomers as SourceCustomerItem
} from "src/api/generated/GetCopyCustomerSourceCustomers";
import styles from "src/screens/dashboard/customers/overview/CustomersOverview.module.css";

type CopyEnvironment = "PROD" | "TEST" | "DEV";
type SearchableSourceCustomer = Pick<SourceCustomerItem, "id" | "name" | "number" | "subdomain">;

interface IComponentProps {
    onCloseModal: () => void;
}

interface ICopyCustomerStartErrorMessages {
    copyJobRunning: string;
    customerExists: string;
    startFailed: (message: string) => string;
}

interface ICopyStatusLabels {
    running: string;
    failed: string;
    alreadyCopied: string;
}

const VISIBLE_CUSTOMER_LIMIT = 12;
const COPY_STATUS_RUNNING = 1;
const COPY_STATUS_COMPLETED = 2;
const COPY_STATUS_FAILED = 3;

const CopyCustomerBase = ({ onCloseModal }: IComponentProps) => {
    const { t } = useTranslation();
    const { authStore, uiStore } = useContext(RootStoreContext);
    const startCopyAbortControllerRef = useRef<AbortController | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [customerSearch, setCustomerSearch] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState<SourceCustomerItem | null>(null);

    const targetEnvironment = getEnvironment().toUpperCase() as CopyEnvironment;
    const sourceEnvironmentOptions = useMemo(() => getSourceEnvironmentOptions(targetEnvironment), [targetEnvironment]);
    const [sourceEnvironment, setSourceEnvironment] = useState<CopyEnvironment>(sourceEnvironmentOptions[0]);

    const { data, loading, error } = useQuery<GetCopyCustomerSourceCustomers>(GET_COPY_CUSTOMER_SOURCE_CUSTOMERS, {
        fetchPolicy: "cache-first"
    });

    const { data: copyJobsData } = useQuery<GetCopyCustomerJobStatuses>(GET_COPY_CUSTOMER_JOB_STATUSES, {
        fetchPolicy: "cache-first"
    });

    const copyCustomerStartErrorMessages = useMemo<ICopyCustomerStartErrorMessages>(
        () => ({
            copyJobRunning: t("screens.dashboard.copy_customer.errors.copy_job_running"),
            customerExists: t("screens.dashboard.copy_customer.errors.customer_exists"),
            startFailed: (message: string) => t("screens.dashboard.copy_customer.errors.start_failed", { message })
        }),
        [t]
    );

    const copyStatusLabels = useMemo<ICopyStatusLabels>(
        () => ({
            running: t("screens.dashboard.copy_customer.status.running"),
            failed: t("screens.dashboard.copy_customer.status.failed"),
            alreadyCopied: t("screens.dashboard.copy_customer.status.already_copied")
        }),
        [t]
    );

    const availableCustomers = useMemo(() => {
        switch (sourceEnvironment) {
            case "DEV":
                return data?.devCustomers ?? [];
            case "TEST":
                return data?.testCustomers ?? [];
            case "PROD":
            default:
                return data?.prodCustomers ?? [];
        }
    }, [data?.devCustomers, data?.prodCustomers, data?.testCustomers, sourceEnvironment]);

    const copyJobStatusByCustomerId = useMemo(() => {
        const statusMap = new Map<string, number>();

        (copyJobsData?.copyJobs ?? []).forEach((job) => {
            statusMap.set(job.customerid, job.status);
        });

        return statusMap;
    }, [copyJobsData?.copyJobs]);

    const filteredCustomers = useMemo(() => {
        const normalizedQuery = customerSearch.trim().toLowerCase();

        if (normalizedQuery.length === 0) {
            return availableCustomers.slice(0, VISIBLE_CUSTOMER_LIMIT);
        }

        return availableCustomers
            .filter((customer) => matchesCustomerSearch(customer, normalizedQuery))
            .slice(0, VISIBLE_CUSTOMER_LIMIT);
    }, [availableCustomers, customerSearch]);

    const handleSourceEnvironmentChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        setSourceEnvironment(event.target.value as CopyEnvironment);
        setCustomerSearch("");
        setSelectedCustomer(null);
        setErrorMessage(undefined);
    }, []);

    const handleCustomerSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomerSearch(event.target.value);
        setErrorMessage(undefined);
    }, []);

    const handleCustomerSelection = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            const customerId = event.currentTarget.dataset.customerId;
            const customer = availableCustomers.find((item) => item.id === customerId);

            if (!customer) {
                return;
            }

            setSelectedCustomer(customer);
            setErrorMessage(undefined);
        },
        [availableCustomers]
    );

    useEffect(() => {
        return () => {
            startCopyAbortControllerRef.current?.abort();
        };
    }, []);

    const handleStartCopy = useCallback(async () => {
        if (isSubmitting) {
            return;
        }

        if (selectedCustomer === null) {
            setErrorMessage(t("screens.dashboard.copy_customer.errors.customer_required"));
            return;
        }

        if (!authStore.user || !authStore.token) {
            uiStore.printStatusMessage(
                t("screens.dashboard.copy_customer.errors.missing_auth"),
                MessageType.ERROR
            );
            return;
        }

        setIsSubmitting(true);
        setErrorMessage(undefined);

        const abortController = new AbortController();
        startCopyAbortControllerRef.current = abortController;
        const tokenType = authStore.tokenType;
        const accessToken = authStore.token;
        const role =
            authStore.user.role === CustomerRole.CUSTOMERSUPPORT ? CustomerRole.CUSTOMER2NDLEVEL : authStore.user.role;

        try {
            const response = await fetch(NetworkConfig.copyCustomerStartUrl, {
                method: "POST",
                headers: {
                    Authorization: `${tokenType} ${accessToken}`,
                    "x-hasura-role": getRoleKey(role),
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    customerId: selectedCustomer.id,
                    sourceEnvironment,
                    targetEnvironment
                }),
                signal: abortController.signal
            });

            if (!response.ok) {
                const result = await tryReadErrorResponse(response);
                throw new Error(result);
            }

            uiStore.printStatusMessage(t("screens.dashboard.copy_customer.started"), MessageType.INFO);
            setIsSubmitting(false);
            onCloseModal();
        } catch (fetchError) {
            if (abortController.signal.aborted) {
                return;
            }

            const resolvedMessage = fetchError instanceof Error ? fetchError.message : String(fetchError);
            const mappedErrorMessage = mapCopyCustomerStartError(resolvedMessage, copyCustomerStartErrorMessages);

            setIsSubmitting(false);
            setErrorMessage(mappedErrorMessage);
            uiStore.printStatusMessage(mappedErrorMessage, MessageType.ERROR);
        } finally {
            if (startCopyAbortControllerRef.current === abortController) {
                startCopyAbortControllerRef.current = null;
            }
        }
    }, [
        authStore.token,
        authStore.tokenType,
        authStore.user,
        copyCustomerStartErrorMessages,
        isSubmitting,
        onCloseModal,
        selectedCustomer,
        sourceEnvironment,
        targetEnvironment,
        t,
        uiStore
    ]);

    if (loading && !data) {
        return <UI.LoadingCard />;
    }

    if (error) {
        return <UI.Alert variant="danger">{error.message}</UI.Alert>;
    }

    return (
        <UI.Form>
            <div className="form-group">
                <label className="d-block mb-2 bold">{t("screens.dashboard.copy_customer.source_environment")}</label>
                <select className="form-control" value={sourceEnvironment} onChange={handleSourceEnvironmentChange}>
                    {sourceEnvironmentOptions.map((environment) => (
                        <option key={environment} value={environment}>
                            {environment}
                        </option>
                    ))}
                </select>
            </div>

            <UI.Input
                label={t("screens.dashboard.copy_customer.target_environment")}
                value={targetEnvironment}
                type="text"
                readOnly={true}
            />

            <UI.Input
                label={t("screens.dashboard.copy_customer.search_customer")}
                value={customerSearch}
                type="text"
                onChange={handleCustomerSearchChange}
                placeholder={t("labels.search")}
            />

            <small className="d-block text-muted mb-3">
                {t("screens.dashboard.copy_customer.search_hint")}
            </small>

            <div className={`${styles.copyCustomerSelectionList} mb-3`}>
                {filteredCustomers.map((customer) => (
                    <button
                        key={customer.id}
                        type="button"
                        data-customer-id={customer.id}
                        onClick={handleCustomerSelection}
                        className={`${styles.copyCustomerSelectionItem} ${
                            selectedCustomer?.id === customer.id ? styles.copyCustomerSelectionItemActive : ""
                        }`}
                    >
                        {renderCopyStatusBadge(copyJobStatusByCustomerId.get(customer.id), copyStatusLabels)}
                        <span className={styles.copyCustomerSelectionName}>
                            {customer.name ?? t("screens.dashboard.copy_customer.no_customer_name")}
                        </span>
                        <span className={styles.copyCustomerSelectionMeta}>
                            {t("screens.dashboard.copy_customer.customer_meta", {
                                subdomain: customer.subdomain ?? "-",
                                id: customer.id,
                                number: customer.number ?? "-"
                            })}
                        </span>
                    </button>
                ))}
                {filteredCustomers.length === 0 ? (
                    <UI.Alert variant="warning" className="mb-0">
                        {t("screens.dashboard.copy_customer.no_customer_found")}
                    </UI.Alert>
                ) : null}
            </div>

            {errorMessage ? <UI.Alert variant="danger">{errorMessage}</UI.Alert> : null}

            <UI.Button
                label={
                    isSubmitting
                        ? t("screens.dashboard.copy_customer.starting")
                        : t("screens.dashboard.copy_customer.start")
                }
                type="button"
                disabled={isSubmitting || selectedCustomer === null}
                onClick={handleStartCopy}
                className="w-100 customer-button"
            />
        </UI.Form>
    );
};

export const matchesCustomerSearch = (customer: SearchableSourceCustomer, normalizedQuery: string) => {
    const searchableValues: (string | null)[] = [customer.id, customer.name, customer.number, customer.subdomain];

    return searchableValues.some((value) => (value ?? "").toLowerCase().includes(normalizedQuery));
};

export const getSourceEnvironmentOptions = (targetEnvironment: CopyEnvironment): CopyEnvironment[] => {
    switch (targetEnvironment) {
        case "DEV":
            return ["PROD", "TEST"];
        case "TEST":
            return ["PROD", "DEV"];
        case "PROD":
        default:
            return ["TEST", "DEV"];
    }
};

export const tryReadErrorResponse = async (response: Response) => {
    try {
        const result = await response.json();
        return result.message ?? result.error ?? `HTTP ${response.status}`;
    } catch {
        return `HTTP ${response.status}`;
    }
};

export const mapCopyCustomerStartError = (message: string, messages: ICopyCustomerStartErrorMessages) => {
    if (message.includes("There is already a copy job for customer")) {
        return messages.copyJobRunning;
    }

    if (message.includes("Customer with Id") && message.includes("already exists")) {
        return messages.customerExists;
    }

    return messages.startFailed(message);
};

export const renderCopyStatusBadge = (status: number | undefined, labels: ICopyStatusLabels) => {
    if (status === undefined) {
        return null;
    }

    if (status === COPY_STATUS_RUNNING) {
        return (
            <span className={`${styles.copyCustomerStatusBadge} ${styles.copyCustomerStatusBadgeRunning}`}>
                {labels.running}
            </span>
        );
    }

    if (status === COPY_STATUS_FAILED) {
        return (
            <span className={`${styles.copyCustomerStatusBadge} ${styles.copyCustomerStatusBadgeFailed}`}>
                {labels.failed}
            </span>
        );
    }

    if (status !== COPY_STATUS_COMPLETED) {
        return null;
    }

    return (
        <span className={`${styles.copyCustomerStatusBadge} ${styles.copyCustomerStatusBadgeDone}`}>
            {labels.alreadyCopied}
        </span>
    );
};

export const CopyCustomer = Sentry.withProfiler(CopyCustomerBase);

